import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subject, takeUntil, debounceTime } from 'rxjs';
import { RequestActions } from '../../store/action/request.action';
import { IRollerCone, PatchRollerConeDto } from '../../interfaces/request.interface';

@Component({
    selector: 'app-rollercone-data',
    templateUrl: './rollercone-data.component.html',
    styleUrl: './rollercone-data.component.css',
    standalone: false
})
export class RollerconeDataComponent implements OnInit, OnDestroy, OnChanges {
    @Input() draftId?: number;
    @Input() rollerConeInitial?: IRollerCone | undefined;
    @Input() readonly: boolean = false;

    public rcForm!: FormGroup;
    private destroy$ = new Subject<void>();

    public inputModeDecimal: { [key: string]: string } = { inputmode: 'decimal' };

    public yesno = [
        { value: true, name: 'Yes' },
        { value: false, name: 'No' }
    ];

    public taskLevelOptions = [
        { id: 2, name: '2 - No Cutters' },
        { id: 3, name: '3' },
        { id: 4, name: '4 - No Cutters' },
        { id: 5, name: '5 - Detached U/S' },
        { id: 6, name: '6 - Full Build' },
        { id: 7, name: '7 - Finished Mold' }
    ];

    constructor(
        private fb: FormBuilder,
        private store: Store
    ) {
        this.rcForm = this.fb.group({
            bitCode: [null],
            bitClass: [null],
            pinAng: [null],
            offsetNum: [null],
            brgCode: [null],
            bkfc: [null],
            taskLevel: [null],
            techBit: [null],
            surfDiamond: [null],
            coneType: [null],
            faiRequired: [false],
            adero: [false],
            newCone: [null],
            newArm: [null],
            layout: [null],
            arm: [null],
            shirtTailProtection: [null],
            referenceLayout: [null],
            referenceArm: [null],
            retrofitFeature: [null],
            retrofitType: [null]
        });
    }

    ngOnInit(): void {
        if (this.rollerConeInitial) {
            this.patchFromInitial(this.rollerConeInitial);
        }

        this.rcForm.valueChanges.pipe(
            debounceTime(200),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            const dto = this.buildDto();
            this.store.dispatch(new RequestActions.PatchRequestDraft({
                draftId: this.draftId,
                changes: { rollerCone: dto }
            }));
            this.store.dispatch(new RequestActions.PatchRequest({
                rollerCone: this.buildFromForm() as any
            }));
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['rollerConeInitial'] && !changes['rollerConeInitial'].firstChange) {
            const rc = changes['rollerConeInitial'].currentValue as IRollerCone | undefined;
            if (rc) {
                this.patchFromInitial(rc);
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private patchFromInitial(rc: IRollerCone): void {
        this.rcForm.patchValue({
            bitCode: rc.bitCode ?? null,
            bitClass: rc.bitClass ?? null,
            pinAng: rc.pinAng ?? null,
            offsetNum: rc.offsetNum ?? null,
            brgCode: rc.brgCode ?? null,
            bkfc: rc.bkfc ?? null,
            taskLevel: rc.taskLevel ?? null,
            techBit: rc.techBit ?? null,
            surfDiamond: rc.surfDiamond ?? null,
            coneType: rc.coneType ?? null,
            faiRequired: rc.faiRequired ?? false,
            adero: rc.adero ?? false,
            newCone: rc.newCone ?? null,
            newArm: rc.newArm ?? null,
            layout: rc.layout ?? null,
            arm: rc.arm ?? null,
            shirtTailProtection: rc.shirtTailProtection ?? null,
            referenceLayout: rc.referenceLayout ?? null,
            referenceArm: rc.referenceArm ?? null,
            retrofitFeature: rc.retrofitFeature ?? null,
            retrofitType: rc.retrofitType ?? null
        }, { emitEvent: false });
    }

    private buildDto(): PatchRollerConeDto {
        const v = this.rcForm.value;
        return {
            id: this.rollerConeInitial?.id ?? 0,
            rtaNum: this.draftId ?? 0,
            bitCode: v.bitCode ?? null,
            bitClass: v.bitClass ?? null,
            pinAng: v.pinAng ?? null,
            offsetNum: v.offsetNum ?? null,
            brgCode: v.brgCode ?? null,
            bkfc: v.bkfc ?? null,
            taskLevel: v.taskLevel ?? null,
            techBit: v.techBit ?? null,
            surfDiamond: v.surfDiamond ?? null,
            coneType: v.coneType ?? null,
            faiRequired: v.faiRequired ?? false,
            adero: v.adero ?? false,
            newCone: v.newCone ?? null,
            newArm: v.newArm ?? null,
            layout: v.layout ?? null,
            arm: v.arm ?? null,
            shirtTailProtection: v.shirtTailProtection ?? null,
            referenceLayout: v.referenceLayout ?? null,
            referenceArm: v.referenceArm ?? null,
            retrofitFeature: v.retrofitFeature ?? null,
            retrofitType: v.retrofitType ?? null
        };
    }

    private buildFromForm(): IRollerCone {
        const v = this.rcForm.value;
        return {
            id: this.rollerConeInitial?.id ?? 0,
            rtaNum: this.draftId ?? 0,
            bitCode: v.bitCode,
            bitClass: v.bitClass,
            pinAng: v.pinAng,
            offsetNum: v.offsetNum,
            brgCode: v.brgCode,
            bkfc: v.bkfc,
            taskLevel: v.taskLevel,
            techBit: v.techBit,
            surfDiamond: v.surfDiamond,
            coneType: v.coneType,
            faiRequired: v.faiRequired,
            adero: v.adero,
            newCone: v.newCone,
            newArm: v.newArm,
            layout: v.layout,
            arm: v.arm,
            shirtTailProtection: v.shirtTailProtection,
            referenceLayout: v.referenceLayout,
            referenceArm: v.referenceArm,
            retrofitFeature: v.retrofitFeature,
            retrofitType: v.retrofitType
        };
    }
}
